// src/screens/LooseStoneReport.jsx
import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaFilter } from "react-icons/fa";
import { FiChevronDown, FiChevronUp, FiSettings, FiX } from "react-icons/fi";
import Select from "react-select";

import { useDispatch, useSelector } from "react-redux";
import { handleDropDowns } from "../config/dropDownHelpers";

/* masters */
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetStoneAttributesQuery } from "../toolkitServices/stoneAttributeApi";

/* store setters */
import { setBranchList } from "../slices/branchSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setStoneAttrList } from "../slices/stoneAttrSlice";

/* report hook */
import { useGetLooseStoneReportQuery } from "../toolkitServices/looseStoneApi";

/* ---------- helpers (match LabelledStockReport vibe) ---------- */
const TZ = "Asia/Kolkata";
const todayYMD = () => {
  const d = new Date();
  const y = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric" }).format(d);
  const m = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, month: "2-digit" }).format(d);
  const dd = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, day: "2-digit" }).format(d);
  return `${y}-${m}-${dd}`;
};
const fmtRangeHuman = (range) => {
  if (!range?.start || !range?.end) return "";
  const s = new Date(range.start);
  const e = new Date(range.end);
  const opt = { timeZone: TZ, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true };
  return `${s.toLocaleString("en-IN", opt)} — ${e.toLocaleString("en-IN", opt)} IST`;
};

const toNum = (v) => Number(v ?? 0) || 0;
// CSV-safe
const fmt = (n, d = 3) => (Number.isFinite(n) ? Number(n).toFixed(d) : "0.000");
const fmt0 = (n) => (Number.isFinite(n) ? Number(n).toFixed(0) : "0");
// UI pretty (Indian)
const nf = (d = 3) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtUI = (n, d = 3) => nf(d).format(Number.isFinite(n) ? Number(n) : 0);
const fmt0UI = (n) => nf(0).format(Number.isFinite(n) ? Number(n) : 0);

const nonEmpty = (s) => typeof s === "string" && s.trim().length > 0;

/* colored block styles (same palette style) */
const BLOCK = {
  opening: { head: "bg-blue-100 text-blue-900", cell: "bg-blue-50" },
  in:      { head: "bg-emerald-100 text-emerald-900", cell: "bg-emerald-50" },
  out:     { head: "bg-rose-100 text-rose-900", cell: "bg-rose-50" },
  closing: { head: "bg-amber-100 text-amber-900", cell: "bg-amber-50" },
};

// consistent width for metric cols
const METRIC_W = "min-w-[110px]";

/* react-select styles */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    borderColor: state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #9481ce" : "none",
    "&:hover": { borderColor: "#b2a6e6" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

/* skeleton row */
const SkeletonRow = ({ cols = 24 }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-3 py-2">
        <div className="h-4 w-full bg-gray-200/70 rounded animate-pulse" />
      </td>
    ))}
  </tr>
);

/* ---------- Component ---------- */
export default function LooseStoneReport() {
  const dispatch = useDispatch();

  // UI state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // date controls
  const [dateMode, setDateMode] = useState("day"); // 'day' | 'range'
  const [date, setDate] = useState(() => todayYMD());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // movement + column toggles (Wt / Pcs)
  const [showMovement, setShowMovement] = useState(false);
  const [cols, setCols] = useState({ wt: true, pcs: true });
  const [dense, setDense] = useState(true);

  // masters
  const { data: branches } = useGetAllBranchesQuery();
  const { data: vendors } = useGetAllVendorsQuery({ page: 1, limit: 200, search: "" });
  const { data: stones } = useGetAllStonesQuery();
  const { data: attrs } = useGetStoneAttributesQuery({}, { refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (stones?.data?.length) dispatch(setStoneList(stones.data));
    if (attrs?.data?.length) dispatch(setStoneAttrList(attrs.data));
  }, [dispatch, branches, vendors, stones, attrs]);

  // store
  const branchesFromState = useSelector((s) => s.branches?.branches);
  const vendorsFromState = useSelector((s) => s.vendors?.vendors);
  const stonesFromState = useSelector((s) => s.stones?.stones);
  const stoneAttrFromState = useSelector((s) => s.stoneAttr?.stoneAttr);

  // filters (applied vs draft)
  const [appliedFilters, setAppliedFilters] = useState({
    branchId: "",
    vendorId: "",
    stoneId: "",
    shape: "",
    color: "",
    size: "",
    settingType: "",
    unit: "", // gram | carat
  });
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  /* options */
  const findOpt = (opts, v) => (opts || []).find((o) => String(o.value) === String(v)) || null;

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState]
  );
  const vendorOptions = useMemo(
    () => handleDropDowns({ key: "vendor", vendors: vendorsFromState }),
    [vendorsFromState]
  );
  const stoneOptions = useMemo(
    () => (stonesFromState || []).map((s) => ({ label: s.stoneName, value: s._id })),
    [stonesFromState]
  );

  const shapeOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "shape" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const colorOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "color" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const sizeOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "size" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const settingTypeOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "settingType" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );

  const unitOptions = useMemo(
    () => [{ label: "gram", value: "gram" }, { label: "carat", value: "carat" }],
    []
  );

  // draft selecteds
  const branchDraft = useMemo(() => findOpt(branchOptions, draftFilters.branchId), [branchOptions, draftFilters.branchId]);
  const vendorDraft = useMemo(() => findOpt(vendorOptions, draftFilters.vendorId), [vendorOptions, draftFilters.vendorId]);
  const stoneDraft = useMemo(() => findOpt(stoneOptions, draftFilters.stoneId), [stoneOptions, draftFilters.stoneId]);
  const shapeDraft = useMemo(() => findOpt(shapeOptions, draftFilters.shape), [shapeOptions, draftFilters.shape]);
  const colorDraft = useMemo(() => findOpt(colorOptions, draftFilters.color), [colorOptions, draftFilters.color]);
  const sizeDraft = useMemo(() => findOpt(sizeOptions, draftFilters.size), [sizeOptions, draftFilters.size]);
  const settingTypeDraft = useMemo(() => findOpt(settingTypeOptions, draftFilters.settingType), [settingTypeOptions, draftFilters.settingType]);
  const unitDraft = useMemo(() => findOpt(unitOptions, draftFilters.unit), [unitOptions, draftFilters.unit]);

  // applied (chips)
  const branchApplied = useMemo(() => findOpt(branchOptions, appliedFilters.branchId), [branchOptions, appliedFilters.branchId]);
  const vendorApplied = useMemo(() => findOpt(vendorOptions, appliedFilters.vendorId), [vendorOptions, appliedFilters.vendorId]);
  const stoneApplied = useMemo(() => findOpt(stoneOptions, appliedFilters.stoneId), [stoneOptions, appliedFilters.stoneId]);
  const shapeApplied = useMemo(() => findOpt(shapeOptions, appliedFilters.shape), [shapeOptions, appliedFilters.shape]);
  const colorApplied = useMemo(() => findOpt(colorOptions, appliedFilters.color), [colorOptions, appliedFilters.color]);
  const sizeApplied = useMemo(() => findOpt(sizeOptions, appliedFilters.size), [sizeOptions, appliedFilters.size]);
  const settingTypeApplied = useMemo(() => findOpt(settingTypeOptions, appliedFilters.settingType), [settingTypeOptions, appliedFilters.settingType]);
  const unitApplied = useMemo(() => findOpt(unitOptions, appliedFilters.unit), [unitOptions, appliedFilters.unit]);

  /* ---------- API: Loose Stone Report ---------- */
  const queryArgs = useMemo(() => {
    const base = {
      movement: showMovement,
      tz: TZ,
      branchId: appliedFilters.branchId || undefined,
      vendorId: appliedFilters.vendorId || undefined,
      stoneId: appliedFilters.stoneId || undefined,
      shape: appliedFilters.shape || undefined,
      color: appliedFilters.color || undefined,
      size: appliedFilters.size || undefined,
      settingType: appliedFilters.settingType || undefined,
      unit: appliedFilters.unit || undefined,
    };
    if (dateMode === "range" && nonEmpty(from) && nonEmpty(to)) return { ...base, from, to };
    return { ...base, date };
  }, [showMovement, appliedFilters, dateMode, date, from, to]);

  const { data, isFetching, isError, error } = useGetLooseStoneReportQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const rows = data?.rows || [];
  const grand = data?.grandTotal || {};
  const humanRange = fmtRangeHuman(data?.range);

  /* ---------- search + paging ---------- */
  const [internalSearch, setInternalSearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setInternalSearch(search), 250); return () => clearTimeout(t); }, [search]);

  const filteredRows = useMemo(() => {
    const q = internalSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r?.branch?.name || "", r?.vendor?.name || "", r?.stone?.name || "",
        r?.shape?.name || "", r?.color?.name || "", r?.size?.name || "",
        r?.settingType?.name || "", r?.unit || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, internalSearch]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const start = (page - 1) * limit;
  const visibleRows = filteredRows.slice(start, start + limit);

  /* ---------- CSV ---------- */
  const exportCSV = () => {
    const head = [
      "Branch","Vendor","Stone","Shape","Color","Size","Setting","Unit",
      ...(cols.wt ? ["Opening Wt"] : []),
      ...(cols.pcs ? ["Opening Pcs"] : []),

      ...(showMovement ? (cols.wt ? ["IN Wt"] : []) : []),
      ...(showMovement ? (cols.pcs ? ["IN Pcs"] : []) : []),

      ...(showMovement ? (cols.wt ? ["OUT Wt"] : []) : []),
      ...(showMovement ? (cols.pcs ? ["OUT Pcs"] : []) : []),

      ...(cols.wt ? ["Closing Wt"] : []),
      ...(cols.pcs ? ["Closing Pcs"] : []),
    ];
    const lines = [head.join(",")];

    visibleRows.forEach((r) => {
      const cell = [
        (r?.branch?.name ?? "").replaceAll(",", " "),
        (r?.vendor?.name ?? "").replaceAll(",", " "),
        (r?.stone?.name ?? "").replaceAll(",", " "),
        (r?.shape?.name ?? "").replaceAll(",", " "),
        (r?.color?.name ?? "").replaceAll(",", " "),
        (r?.size?.name ?? "").replaceAll(",", " "),
        (r?.settingType?.name ?? "").replaceAll(",", " "),
        (r?.unit ?? "").replaceAll(",", " "),
      ];
      const pushPack = (p) => {
        if (cols.wt) cell.push(fmt(toNum(p?.wt)));
        if (cols.pcs) cell.push(fmt0(toNum(p?.pcs)));
      };
      pushPack(r.opening || {});
      if (showMovement) pushPack(r.in || {});
      if (showMovement) pushPack(r.out || {});
      pushPack(r.closing || {});
      lines.push(cell.join(","));
    });

    const g = grand || {};
    const footer = ["GRAND","","","","","","",""];
    const pushG = (p) => {
      if (cols.wt) footer.push(fmt(toNum(p?.wt)));
      if (cols.pcs) footer.push(fmt0(toNum(p?.pcs)));
    };
    pushG(g.opening || {});
    if (showMovement) pushG(g.in || {});
    if (showMovement) pushG(g.out || {});
    pushG(g.closing || {});
    lines.push(footer.join(","));

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = (dateMode === "range" && from && to) ? `${from}_to_${to}` : `${date}`;
    a.download = `loose-stone-report_${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  /* ---------- modal helpers ---------- */
  const openFilters = () => { setDraftFilters(appliedFilters); setIsFilterOpen(true); };
  const applyFilters = () => { setAppliedFilters(draftFilters); setPage(1); setIsFilterOpen(false); };
  const resetDraft = () => setDraftFilters({
    branchId: "", vendorId: "", stoneId: "", shape: "", color: "", size: "", settingType: "", unit: "",
  });

  const onDraft = (key) => (opt) => setDraftFilters((s) => ({ ...s, [key]: opt?.value || "" }));

  // counts
  const metricColsCount = (cols.wt ? 1 : 0) + (cols.pcs ? 1 : 0);
  const totalCols =
    8 + // identity
    metricColsCount + // opening
    (showMovement ? metricColsCount : 0) + // IN
    (showMovement ? metricColsCount : 0) + // OUT
    metricColsCount; // closing

  /* ---------- UI ---------- */
  return (
    <div className="relative rounded-2xl shadow-xl border bg-white max-w-7xl mx-auto">
      {/* Header */}
      <div className="px-4 py-4 border-b bg-white">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Loose Stone Report</h2>
            {humanRange && (
              <div className="mt-0.5 text-xs text-gray-500">
                Range: <span className="font-medium text-gray-700">{humanRange}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
              <input
                className="bg-transparent px-2 py-1 text-sm placeholder-gray-500 focus:outline-none"
                placeholder="Search (branch/vendor/stone/shape/color/size/setting/unit)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Date mode */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-2 py-1">
              <button
                onClick={() => setDateMode("day")}
                className={`px-2 py-1 rounded text-sm ${dateMode === "day" ? "bg-white shadow text-gray-800" : "text-gray-600"}`}
                title="Single Day"
              >
                Day
              </button>
              <button
                onClick={() => setDateMode("range")}
                className={`px-2 py-1 rounded text-sm ${dateMode === "range" ? "bg-white shadow text-gray-800" : "text-gray-600"}`}
                title="Date Range"
              >
                Range
              </button>
            </div>

            {/* Date pickers */}
            {dateMode === "day" ? (
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="px-2 py-1 rounded bg-white border text-gray-700"
              />
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="px-2 py-1 rounded bg-white border text-gray-700"
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="px-2 py-1 rounded bg-white border text-gray-700"
                />
              </div>
            )}

            {/* movement toggle */}
            <button
              onClick={() => setShowMovement((v) => !v)}
              className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition text-sm"
              title="Toggle IN/OUT"
            >
              {showMovement ? "Hide IN/OUT" : "Show IN/OUT"}
            </button>

            {/* column picker (optional) */}
            {/* <ColumnPicker cols={cols} setCols={setCols} dense={dense} setDense={setDense} /> */}

            {/* Filters */}
            <button
              onClick={openFilters}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-black transition text-sm"
              title="Filters"
            >
              <FaFilter />
              <span>Filters</span>
            </button>

            {/* Export */}
            <button
              onClick={exportCSV}
              disabled={!visibleRows.length}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-b bg-gray-50 flex flex-wrap items-center gap-2 text-xs">
        <LegendBadge label="Opening" cls={BLOCK.opening.head} />
        <LegendBadge label="IN" cls={BLOCK.in.head} />
        <LegendBadge label="OUT" cls={BLOCK.out.head} />
        <LegendBadge label="Closing" cls={BLOCK.closing.head} />
        <span className="text-gray-400 ml-2">• numbers: Indian format •</span>
      </div>

      {/* Applied chips */}
      <AnimatePresence initial={false}>
        {(appliedFilters.branchId ||
          appliedFilters.vendorId ||
          appliedFilters.stoneId ||
          appliedFilters.shape ||
          appliedFilters.color ||
          appliedFilters.size ||
          appliedFilters.settingType ||
          appliedFilters.unit) && (
          <motion.div
            initial={{opacity:0,y:-6}}
            animate={{opacity:1,y:0}}
            exit={{opacity:0,y:-6}}
            className="px-4 py-2 bg-white border-b flex flex-wrap gap-2 items-center"
          >
            <span className="text-xs text-gray-500">Applied filters:</span>

            {branchApplied && <Chip label={`Branch: ${branchApplied.label}`} onClear={() => clearOne("branchId", setAppliedFilters, setPage)} />}
            {vendorApplied && <Chip label={`Vendor: ${vendorApplied.label}`} onClear={() => clearOne("vendorId", setAppliedFilters, setPage)} />}
            {stoneApplied && <Chip label={`Stone: ${stoneApplied.label}`} onClear={() => clearOne("stoneId", setAppliedFilters, setPage)} />}
            {shapeApplied && <Chip label={`Shape: ${shapeApplied.label}`} onClear={() => clearOne("shape", setAppliedFilters, setPage)} />}
            {colorApplied && <Chip label={`Color: ${colorApplied.label}`} onClear={() => clearOne("color", setAppliedFilters, setPage)} />}
            {sizeApplied && <Chip label={`Size: ${sizeApplied.label}`} onClear={() => clearOne("size", setAppliedFilters, setPage)} />}
            {settingTypeApplied && <Chip label={`Setting: ${settingTypeApplied.label}`} onClear={() => clearOne("settingType", setAppliedFilters, setPage)} />}
            {unitApplied && <Chip label={`Unit: ${unitApplied.label}`} onClear={() => clearOne("unit", setAppliedFilters, setPage)} />}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls */}
      <div className="px-4 py-2 flex flex-wrap gap-3 items-center justify-between bg-white border-b">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Rows:</span>
          <select
            className="border rounded-md px-2 py-1"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
          >
            {[10,20,50,100].map((n)=><option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-gray-500">• Total: <b>{total}</b></span>
        </div>

        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50" disabled={!canPrev || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span className="text-sm text-gray-600">Page <b>{page}</b> / {totalPages}</span>
          <button className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50" disabled={!canNext || isFetching} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      </div>

      {/* Table wrapper — horizontal scroll only */}
      <div className="overflow-x-auto w-full">
        <table className={`w-full text-sm ${dense ? "leading-tight" : "leading-normal"} min-w-[1200px]`}>
          <thead className="uppercase text-xs sticky top-0 z-10 shadow-sm">
            {/* Group headers */}
            <tr className="whitespace-nowrap bg-gray-100 text-gray-700">
              <th className="px-3 py-2 text-left">Branch</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Stone</th>
              <th className="px-3 py-2 text-left">Shape</th>
              <th className="px-3 py-2 text-left">Color</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-left">Setting</th>
              <th className="px-3 py-2 text-left">Unit</th>

              <th className={`px-3 py-2 text-center font-semibold ${BLOCK.opening.head}`} colSpan={metricColsCount}>Opening</th>
              {showMovement && <th className={`px-3 py-2 text-center font-semibold ${BLOCK.in.head}`} colSpan={metricColsCount}>IN</th>}
              {showMovement && <th className={`px-3 py-2 text-center font-semibold ${BLOCK.out.head}`} colSpan={metricColsCount}>OUT</th>}
              <th className={`px-3 py-2 text-center font-semibold ${BLOCK.closing.head}`} colSpan={metricColsCount}>Closing</th>
            </tr>

            {/* Column headers with block colors */}
            <tr className="whitespace-nowrap">
              {/* identity placeholders */}
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />
              <th className="px-3 py-2 bg-gray-100" />

              {renderMetricHeads(cols, `${BLOCK.opening.head} ${METRIC_W}`)}
              {showMovement && renderMetricHeads(cols, `${BLOCK.in.head} ${METRIC_W}`)}
              {showMovement && renderMetricHeads(cols, `${BLOCK.out.head} ${METRIC_W}`)}
              {renderMetricHeads(cols, `${BLOCK.closing.head} ${METRIC_W}`)}
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            {isError && (
              <tr>
                <td colSpan={totalCols} className="px-4 py-6 text-red-600 whitespace-normal">
                  {error?.data?.msg || error?.data?.message || "Failed to load report"}
                </td>
              </tr>
            )}

            {isFetching && Array.from({ length: 8 }).map((_, i) => (<SkeletonRow key={`sk-${i}`} cols={totalCols} />))}

            {!isFetching && !visibleRows.length && !isError && (
              <tr>
                <td colSpan={totalCols} className="px-4 py-6 text-center text-gray-500">No records found.</td>
              </tr>
            )}

            {!isFetching && (
              <AnimatePresence>
                {visibleRows.map((r, index) => {
                  const zebra = index % 2 === 0 ? "bg-white" : "bg-gray-50/60";
                  return (
                    <motion.tr
                      key={`${r?.branch?.id || "-"}-${r?.vendor?.id || "-"}-${r?.stone?.id || "-"}-${r?.shape?.id || "-"}-${r?.color?.id || "-"}-${r?.size?.id || "-"}-${r?.settingType?.id || "-"}-${r?.unit || "-"}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.18, delay: index * 0.01 }}
                      className={`${zebra} border-t hover:bg-violet-50/60 transition whitespace-nowrap`}
                    >
                      <td className="px-3 py-2">{r?.branch?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.vendor?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.stone?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.shape?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.color?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.size?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.settingType?.name || "-"}</td>
                      <td className="px-3 py-2">{r?.unit || "-"}</td>

                      {renderMetricCells(r?.opening, cols, `${BLOCK.opening.cell} ${METRIC_W}`)}
                      {showMovement && renderMetricCells(r?.in, cols, `${BLOCK.in.cell} ${METRIC_W}`)}
                      {showMovement && renderMetricCells(r?.out, cols, `${BLOCK.out.cell} ${METRIC_W}`)}
                      {renderMetricCells(r?.closing, cols, `${BLOCK.closing.cell} ${METRIC_W}`)}
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            )}
          </tbody>

          {/* GRAND footer */}
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-gray-100 border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)] whitespace-nowrap">
                <td className="px-3 py-3 font-semibold text-gray-800">GRAND</td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />

                {renderMetricCells(grand?.opening, cols, `${BLOCK.opening.cell} ${METRIC_W}`, true)}
                {showMovement && renderMetricCells(grand?.in, cols, `${BLOCK.in.cell} ${METRIC_W}`, true)}
                {showMovement && renderMetricCells(grand?.out, cols, `${BLOCK.out.cell} ${METRIC_W}`, true)}
                {renderMetricCells(grand?.closing, cols, `${BLOCK.closing.cell} ${METRIC_W}`, true)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Slim summary bar */}
      {rows.length > 0 && (
        <div className="w-full bg-white border-t">
          <div className="px-4 py-3 flex flex-wrap gap-3 items-center">
            <Badge label={`Opening Wt: ${fmtUI(toNum(grand?.opening?.wt))}`} />
            <Badge label={`Opening Pcs: ${fmt0UI(toNum(grand?.opening?.pcs))}`} />
            <Badge label={`Closing Wt: ${fmtUI(toNum(grand?.closing?.wt))}`} />
            <Badge label={`Closing Pcs: ${fmt0UI(toNum(grand?.closing?.pcs))}`} />
            {showMovement && <Badge label={`IN Wt: ${fmtUI(toNum(grand?.in?.wt))}`} />}
            {showMovement && <Badge label={`OUT Wt: ${fmtUI(toNum(grand?.out?.wt))}`} />}
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {isFilterOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <motion.div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-3xl" initial={{opacity:0,y:-40}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-40}}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button onClick={resetDraft} className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50">Reset</button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Branch</label>
                <Select isClearable styles={selectStyles} options={branchOptions} value={branchDraft} onChange={onDraft("branchId")} placeholder="Select branch…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Vendor</label>
                <Select isClearable styles={selectStyles} options={vendorOptions} value={vendorDraft} onChange={onDraft("vendorId")} placeholder="Select vendor…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Stone</label>
                <Select isClearable styles={selectStyles} options={stoneOptions} value={stoneDraft} onChange={onDraft("stoneId")} placeholder="Select stone…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Shape</label>
                <Select isClearable styles={selectStyles} options={shapeOptions} value={shapeDraft} onChange={onDraft("shape")} placeholder="Select shape…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Color</label>
                <Select isClearable styles={selectStyles} options={colorOptions} value={colorDraft} onChange={onDraft("color")} placeholder="Select color…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Size</label>
                <Select isClearable styles={selectStyles} options={sizeOptions} value={sizeDraft} onChange={onDraft("size")} placeholder="Select size…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Setting Type</label>
                <Select isClearable styles={selectStyles} options={settingTypeOptions} value={settingTypeDraft} onChange={onDraft("settingType")} placeholder="Select setting…" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Unit</label>
                <Select isClearable styles={selectStyles} options={unitOptions} value={unitDraft} onChange={onDraft("unit")} placeholder="gram / carat" />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setIsFilterOpen(false)} className="px-4 py-2 rounded-md bg-gray-200 text-gray-800">Cancel</button>
              <button onClick={applyFilters} className="px-4 py-2 rounded-md bg-[#9481ce] text-white">Apply & Run</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ---------- utils ---------- */
function clearOne(key, setAppliedFilters, setPage) {
  setAppliedFilters((prev) => ({ ...prev, [key]: "" }));
  setPage(1);
}

/* ---------- renderers (match LabelledStockReport patterns) ---------- */
function renderMetricHeads(cols, cls = "") {
  const th = (k, label) => <th key={k} className={`px-3 py-2 text-right ${cls}`}>{label}</th>;
  const heads = [];
  if (cols.wt) heads.push(th("wt","Wt"));
  if (cols.pcs) heads.push(th("pcs","Pcs"));
  return heads;
}
function renderMetricCells(pack, cols, cls = "", strong = false) {
  const v = (x, d = 3) => (strong ? <b>{fmtUI(x, d)}</b> : fmtUI(x, d));
  const v0 = (x) => (strong ? <b>{fmt0UI(x)}</b> : fmt0UI(x));
  const td = (k, child) => <td key={k} className={`px-3 py-2 text-right ${cls}`}>{child}</td>;
  const tds = [];
  if (cols.wt) tds.push(td("wt", v(toNum(pack?.wt))));
  if (cols.pcs) tds.push(td("pcs", v0(toNum(pack?.pcs))));
  return tds;
}

/* ---------- small UI bits ---------- */
function ColumnPicker({ cols, setCols, dense, setDense }) {
  const [open, setOpen] = useState(false);
  const toggle = (k) => setCols((c) => ({ ...c, [k]: !c[k] }));
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm"
      >
        <FiSettings /><span>Columns</span>{open ? <FiChevronUp /> : <FiChevronDown />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-white shadow-xl z-50 p-2">
          {[
            ["wt","Weight"],["pcs","Pieces"],
          ].map(([key,label])=>(
            <label key={key} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-50 rounded">
              <input type="checkbox" checked={!!cols[key]} onChange={()=>toggle(key)} />{label}
            </label>
          ))}

          <div className="h-px my-2 bg-gray-200" />
          <label className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-50 rounded">
            <input type="checkbox" checked={dense} onChange={()=>setDense((d)=>!d)} />
            Compact rows
          </label>
        </div>
      )}
    </div>
  );
}

function Badge({ label }) {
  return <span className="inline-flex items-center text-xs bg-violet-50 text-violet-700 border border-violet-200 px-2 py-1 rounded">{label}</span>;
}
function Chip({ label, onClear }) {
  return (
    <motion.span initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.9,opacity:0}} className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 border border-violet-200 px-2 py-1 rounded-full">
      {label}<button onClick={onClear} className="hover:text-violet-900"><FiX /></button>
    </motion.span>
  );
}
function LegendBadge({ label, cls }) {
  return (
    <span className={`inline-flex items-center gap-2 px-2 py-1 rounded border text-[11px] ${cls}`}>
      <span className="h-2 w-2 rounded-full bg-current/60" />
      {label}
    </span>
  );
}
